import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Colors, URLs } from '../../constants';
import { ProChurchToolsH } from '../../icons/Icons';
import { ButtonInverse, ContentContainer, SmallTopNotch } from '../GlobalStyles';
import { font, media } from '../style-utils';

const handleContactSupportClick = (e) => {
  if (window.Beacon !== undefined) {
    window.Beacon('open');
  } else {
    window.location = 'mailto:hello@prochurchtools.com';
  }
};

export const FooterHelp = (props) => (
  <Fragment>
    <HelpSection>
      <HelpContentContainer>
        <div>
          <HelpHeading>Have a question or want to talk to&nbsp;someone?</HelpHeading>
          <HelpText>
            <p>
              Go ahead and say hi! We’re real people here - and we’d love to help answer any questions you
              may&nbsp;have.
            </p>
          </HelpText>
        </div>
        <div>
          <HelpButtonInverse onClick={handleContactSupportClick}>Say Hello</HelpButtonInverse>
        </div>
      </HelpContentContainer>
    </HelpSection>
  </Fragment>
);

export const FooterCta = ({
  paragraph = 'Start setting up Nucleus for your church today and try everything free. We don’t even require a credit card!',
  button = 'Join Now',
  link = '#pricing',
  ...props
}) => (
  <Fragment>
    <FinalCtaSection>
      <SmallTopNotch color={Colors.PrimaryDark} />
      <FinalCtaContentContainer>
        <FinalCtaHeading
          style={{
            maxWidth: props.headingMaxWidth,
            marginBottom: props.headingMarginBottom,
          }}
        >
          We’re fully committed to your church’s success. That’s why The 167 mentorship program comes with a{' '}
          <U>30-day&nbsp;guarantee</U>.
        </FinalCtaHeading>
        <FinalCtaText>
          <p>
            If you’re not satisfied with the program in any way in the first 30 days, we’ll offer you a 100% refund on
            your purchase. It’s as easy as that.
          </p>
        </FinalCtaText>
        {/* {cta && <FinalCtaPrice dangerouslySetInnerHTML={{ __html: cta }} />} */}
        {button && (
          <FinalCtaButton>
            <ButtonInverse forwardedAs="a" href={link || URLs.register} color={Colors.PrimaryDark}>
              {button}
            </ButtonInverse>
          </FinalCtaButton>
        )}
      </FinalCtaContentContainer>
    </FinalCtaSection>
  </Fragment>
);

export const FooterBar = (props) => (
  <Fragment>
    <Footer>
      <FooterContainer>
        <FooterLogo color="black" />
        <div>
          <FooterSectionLabel>Legal Info</FooterSectionLabel>
          <FooterSectionText>
            <FooterSectionLink href="https://prochurchtools.com/privacy-policy">Privacy Policy</FooterSectionLink>
            {', '}
            <FooterSectionLink href="https://prochurchtools.com/terms/">Terms of Service</FooterSectionLink>
          </FooterSectionText>
        </div>
        <div>
          <FooterSectionLabel>Copyright 2016-{new Date().getFullYear()}</FooterSectionLabel>
          <FooterSectionText>
            <FooterSectionLink href="https://prochurchtools.com" target="_blank">
              Pro Church Tools, Inc.
            </FooterSectionLink>
          </FooterSectionText>
        </div>
      </FooterContainer>
    </Footer>
  </Fragment>
);

const FullFooter = ({ showCta = true, showHelp = true, showBar = true }) => (
  <Fragment>
    {showCta && <FooterCta />}
    {showHelp && <FooterHelp />}
    {showBar && <FooterBar />}
  </Fragment>
);

export default FullFooter;

// Styled Globals
const SectionHeading = styled.div`
  ${font(42, 'Medium', 42, -0.75)};
  text-align: center;

  ${media.desktopAndUp`
    font-size: 5.6rem;
  `};
`;
const SectionText = styled.div`
  color: ${Colors.PrimaryDark};
  p {
    ${font(20, 'Book', 28, -0.5)};
    color: inherit;
    margin-top: 1.4em;

    ${media.desktopAndUp`
      font-size: 2.2rem;
    `};
  }
`;

// Styled FinalCta
const FinalCtaSection = styled.section`
  position: relative;
  padding: 10rem 0;
  margin-top: 8rem;
  background-color: ${Colors.PrimaryDark};
  text-align: center;
`;

const FinalCtaContentContainer = styled(ContentContainer)``;

const FinalCtaHeading = styled(SectionHeading)`
  font-weight: bold;
  color: #ffffff;
  max-width: 16em;
  margin: 0 auto;

  ${media.desktopAndUp`
    font-size: 5.6rem;
  `};
`;

const FinalCtaText = styled(SectionText)`
  opacity: 0.8;
  color: #ffffff;
  max-width: 36rem;
  margin: 0 auto;
`;

const FinalCtaButton = styled.div`
  margin-top: 4rem;
`;

const HelpSection = styled.div`
  background: ${Colors.Primary};
  padding: 12rem 2rem;
`;
const HelpContentContainer = styled(ContentContainer)`
  @media (max-width: 899px) {
    text-align: center;
    & {
      max-width: 60rem;
    }
  }

  @media (min-width: 900px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
  }
`;

const HelpHeading = styled.div`
  ${font(36, 'Medium', 40, -0.5)};
  color: #ffffff;
  margin-bottom: 1em;
  max-width: 11em;
`;

const HelpText = styled.div`
  p {
    ${font(20, 'Book', 28, -0.5)};
    color: var(--colorWhite);
    margin: 0 auto 2em;
    max-width: 40rem;

    @media (min-width: 900px) {
      margin: 0;
    }
  }
`;

const HelpButtonInverse = styled(ButtonInverse)``;

const Footer = styled.footer`
  background: #ffffff;
  padding: 6rem 0 11.2rem;
  text-align: left;

  ${media.tabletLandscapeAndUp`
    padding: 6rem 0;
  `};
`;

const FooterContainer = styled(ContentContainer)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 auto;
  padding: 0 5rem;
  text-align: center;

  ${media.tabletLandscapeAndUp`
    text-align: left;
  `};

  & > div {
    flex: 0 0 auto;
    width: 100%;

    &:first-of-type {
      margin-bottom: 3.4rem;
    }

    ${media.tabletLandscapeAndUp`
      width: auto;
      &:first-of-type {
        margin-bottom: 0;
      }
    `};
  }
`;

const FooterLogo = styled(ProChurchToolsH)`
  flex: 0 0 auto;
  width: 26rem;
  margin: 0 auto 4rem;

  ${media.tabletLandscapeAndUp`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `};
`;

const FooterSectionLabel = styled.div`
  ${font(12, 'Medium', 12, 1.5)};
  color: var(--colorBlack);
  text-transform: uppercase;
  opacity: 0.5;
  margin-bottom: 0.2rem;
`;

const FooterSectionText = styled.div`
  ${font(18, 'Book', 21, 0)};
  color: var(--colorBlack);
`;

const FooterSectionLink = styled.a`
  ${font(18, 'Bold', 21, 0)};
  color: var(--colorBlack);
  transition: opacity 0.15s ease-out;

  &:hover {
    opacity: 0.75;
    text-decoration: none;
  }
`;

const U = styled.span`
  text-decoration: underline;
`;



// WEBPACK FOOTER //
// ./src/components/Footer/Footer.js